import React, { useState } from 'react';
import { useConfig } from '../../contexts/ConfigContext';
import { updateConfiguration } from '../../services/api';
import { trackAdminAction } from '../../utils/analytics';
import { isValidHexColor, isValidUrl } from '../../utils/validators';
import Button from '../common/Button';
import Input from '../common/Input';

interface ConfigurationFormProps {
  section: 'general' | 'header' | 'content' | 'footer' | 'vanta' | 'seo';
}

const ConfigurationForm: React.FC<ConfigurationFormProps> = ({ section }) => {
  const { config, updateConfig } = useConfig();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Create a copy of the config for form state
  const [formData, setFormData] = useState({
    siteSettings: { ...config.siteSettings },
    footerSettings: { ...config.footerSettings },
    vantaSettings: { ...config.vantaSettings },
    seoSettings: { ...config.seoSettings }
  });
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    
    // Handle nested properties
    if (name.includes('.')) {
      const parts = name.split('.');
      
      if (parts.length === 3) {
        const [section, subsection, field] = parts;
        
        setFormData(prev => {
          const sectionData = prev[section as keyof typeof prev] as Record<string, any>;
          const subsectionData = sectionData[subsection] as Record<string, any>;
          
          return {
            ...prev,
            [section]: {
              ...sectionData,
              [subsection]: {
                ...subsectionData,
                [field]: type === 'number' ? parseFloat(value) : type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
              }
            }
          };
        });
      } else {
        const [section, field] = parts;
        
        setFormData(prev => {
          const sectionData = prev[section as keyof typeof prev] as Record<string, any>;
          
          return {
            ...prev,
            [section]: {
              ...sectionData,
              [field]: type === 'number' ? parseFloat(value) : type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
            }
          };
        });
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'number' ? parseFloat(value) : type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
      }));
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    setError(null);
    
    try {
      // Update configuration in Firestore
      await updateConfiguration(formData);
      
      // Update local state
      updateConfig(formData);
      
      // Track admin action
      trackAdminAction('update_configuration', { section });
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error updating configuration:', err);
      setError('Failed to update configuration. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const renderGeneralSettings = () => (
    <>
      <h3 className="text-lg font-medium mb-4">General Settings</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <h4 className="font-medium mb-2">Colors</h4>
          <Input
            type="text"
            id="backgroundColor"
            name="siteSettings.colors.backgroundColor"
            value={formData.siteSettings.colors.backgroundColor}
            onChange={handleInputChange}
            label="Background Color"
            placeholder="#ffffff"
            error={!isValidHexColor(formData.siteSettings.colors.backgroundColor) ? 'Please enter a valid hex color' : ''}
          />
          
          <Input
            type="text"
            id="textColor"
            name="siteSettings.colors.textColor"
            value={formData.siteSettings.colors.textColor}
            onChange={handleInputChange}
            label="Text Color"
            placeholder="#000000"
            error={!isValidHexColor(formData.siteSettings.colors.textColor) ? 'Please enter a valid hex color' : ''}
          />
          
          <Input
            type="text"
            id="accentColor"
            name="siteSettings.colors.accentColor"
            value={formData.siteSettings.colors.accentColor}
            onChange={handleInputChange}
            label="Accent Color"
            placeholder="#4255a4"
            error={!isValidHexColor(formData.siteSettings.colors.accentColor) ? 'Please enter a valid hex color' : ''}
          />
        </div>
        
        <div>
          <h4 className="font-medium mb-2">Fonts</h4>
          <Input
            type="text"
            id="headingFont"
            name="siteSettings.fonts.headingFont"
            value={formData.siteSettings.fonts.headingFont}
            onChange={handleInputChange}
            label="Heading Font"
            placeholder="sans-serif"
          />
          
          <Input
            type="text"
            id="bodyFont"
            name="siteSettings.fonts.bodyFont"
            value={formData.siteSettings.fonts.bodyFont}
            onChange={handleInputChange}
            label="Body Font"
            placeholder="sans-serif"
          />
        </div>
      </div>
    </>
  );
  
  const renderHeaderSettings = () => (
    <>
      <h3 className="text-lg font-medium mb-4">Header Settings</h3>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Main Heading Type
        </label>
        <div className="flex space-x-4">
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="siteSettings.mainHeading.type"
              value="text"
              checked={formData.siteSettings.mainHeading.type === 'text'}
              onChange={handleInputChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2">Text</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="siteSettings.mainHeading.type"
              value="image"
              checked={formData.siteSettings.mainHeading.type === 'image'}
              onChange={handleInputChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2">Image</span>
          </label>
        </div>
      </div>
      
      {formData.siteSettings.mainHeading.type === 'text' ? (
        <Input
          type="text"
          id="headingText"
          name="siteSettings.mainHeading.textContent"
          value={formData.siteSettings.mainHeading.textContent}
          onChange={handleInputChange}
          label="Heading Text"
          placeholder="Coming Soon"
        />
      ) : (
        <Input
          type="text"
          id="headingImage"
          name="siteSettings.mainHeading.imageUrl"
          value={formData.siteSettings.mainHeading.imageUrl}
          onChange={handleInputChange}
          label="Heading Image URL"
          placeholder="https://example.com/logo.png"
          error={formData.siteSettings.mainHeading.imageUrl && !isValidUrl(formData.siteSettings.mainHeading.imageUrl) ? 'Please enter a valid URL' : ''}
        />
      )}
      
      <Input
        type="text"
        id="tagline"
        name="siteSettings.tagline"
        value={formData.siteSettings.tagline}
        onChange={handleInputChange}
        label="Tagline"
        placeholder="Our new website is on its way"
      />
    </>
  );
  
  const renderContentSettings = () => (
    <>
      <h3 className="text-lg font-medium mb-4">Content Settings</h3>
      
      <div className="mb-4">
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          id="description"
          name="siteSettings.description"
          value={formData.siteSettings.description}
          onChange={handleInputChange}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="We are working on something amazing. Stay tuned for updates!"
        />
      </div>
      
      <Input
        type="text"
        id="ctaText"
        name="siteSettings.callToAction.text"
        value={formData.siteSettings.callToAction.text}
        onChange={handleInputChange}
        label="Call to Action Text"
        placeholder="Subscribe to get notified when we launch"
      />
      
      <Input
        type="text"
        id="ctaButton"
        name="siteSettings.callToAction.buttonText"
        value={formData.siteSettings.callToAction.buttonText}
        onChange={handleInputChange}
        label="Call to Action Button Text"
        placeholder="Subscribe"
      />
      
      <Input
        type="text"
        id="launchInfo"
        name="siteSettings.launchInfo"
        value={formData.siteSettings.launchInfo}
        onChange={handleInputChange}
        label="Launch Information"
        placeholder="Launching soon"
      />
    </>
  );
  
  const renderFooterSettings = () => (
    <>
      <h3 className="text-lg font-medium mb-4">Footer Settings</h3>
      
      <div className="mb-6">
        <h4 className="font-medium mb-2">Social Media Links</h4>
        <Input
          type="text"
          id="socialX"
          name="footerSettings.socialLinks.x"
          value={formData.footerSettings.socialLinks.x}
          onChange={handleInputChange}
          label="X (Twitter)"
          placeholder="https://x.com/yourusername"
          error={formData.footerSettings.socialLinks.x && !isValidUrl(formData.footerSettings.socialLinks.x) ? 'Please enter a valid URL' : ''}
        />
        
        <Input
          type="text"
          id="socialInstagram"
          name="footerSettings.socialLinks.instagram"
          value={formData.footerSettings.socialLinks.instagram}
          onChange={handleInputChange}
          label="Instagram"
          placeholder="https://instagram.com/yourusername"
          error={formData.footerSettings.socialLinks.instagram && !isValidUrl(formData.footerSettings.socialLinks.instagram) ? 'Please enter a valid URL' : ''}
        />
        
        <Input
          type="text"
          id="socialTiktok"
          name="footerSettings.socialLinks.tiktok"
          value={formData.footerSettings.socialLinks.tiktok}
          onChange={handleInputChange}
          label="TikTok"
          placeholder="https://tiktok.com/@yourusername"
          error={formData.footerSettings.socialLinks.tiktok && !isValidUrl(formData.footerSettings.socialLinks.tiktok) ? 'Please enter a valid URL' : ''}
        />
        
        <Input
          type="text"
          id="socialFacebook"
          name="footerSettings.socialLinks.facebook"
          value={formData.footerSettings.socialLinks.facebook}
          onChange={handleInputChange}
          label="Facebook"
          placeholder="https://facebook.com/yourpage"
          error={formData.footerSettings.socialLinks.facebook && !isValidUrl(formData.footerSettings.socialLinks.facebook) ? 'Please enter a valid URL' : ''}
        />
        
        <Input
          type="text"
          id="socialYoutube"
          name="footerSettings.socialLinks.youtube"
          value={formData.footerSettings.socialLinks.youtube}
          onChange={handleInputChange}
          label="YouTube"
          placeholder="https://youtube.com/c/yourchannel"
          error={formData.footerSettings.socialLinks.youtube && !isValidUrl(formData.footerSettings.socialLinks.youtube) ? 'Please enter a valid URL' : ''}
        />
        
        <Input
          type="text"
          id="socialLinkedin"
          name="footerSettings.socialLinks.linkedin"
          value={formData.footerSettings.socialLinks.linkedin}
          onChange={handleInputChange}
          label="LinkedIn"
          placeholder="https://linkedin.com/company/yourcompany"
          error={formData.footerSettings.socialLinks.linkedin && !isValidUrl(formData.footerSettings.socialLinks.linkedin) ? 'Please enter a valid URL' : ''}
        />
      </div>
      
      <div>
        <h4 className="font-medium mb-2">Legal Information</h4>
        <Input
          type="text"
          id="copyright"
          name="footerSettings.legalInfo.copyright"
          value={formData.footerSettings.legalInfo.copyright}
          onChange={handleInputChange}
          label="Copyright Text"
          placeholder="© 2025 Your Company. All rights reserved."
        />
        
        <Input
          type="text"
          id="privacyPolicy"
          name="footerSettings.legalInfo.privacyPolicy"
          value={formData.footerSettings.legalInfo.privacyPolicy}
          onChange={handleInputChange}
          label="Privacy Policy Link"
          placeholder="https://example.com/privacy"
          error={formData.footerSettings.legalInfo.privacyPolicy && !isValidUrl(formData.footerSettings.legalInfo.privacyPolicy) ? 'Please enter a valid URL' : ''}
        />
        
        <Input
          type="text"
          id="termsConditions"
          name="footerSettings.legalInfo.termsConditions"
          value={formData.footerSettings.legalInfo.termsConditions}
          onChange={handleInputChange}
          label="Terms & Conditions Link"
          placeholder="https://example.com/terms"
          error={formData.footerSettings.legalInfo.termsConditions && !isValidUrl(formData.footerSettings.legalInfo.termsConditions) ? 'Please enter a valid URL' : ''}
        />
        
        <Input
          type="text"
          id="disclaimer"
          name="footerSettings.legalInfo.disclaimer"
          value={formData.footerSettings.legalInfo.disclaimer}
          onChange={handleInputChange}
          label="Disclaimer Link"
          placeholder="https://example.com/disclaimer"
          error={formData.footerSettings.legalInfo.disclaimer && !isValidUrl(formData.footerSettings.legalInfo.disclaimer) ? 'Please enter a valid URL' : ''}
        />
      </div>
    </>
  );
  
  const renderVantaSettings = () => (
    <>
      <h3 className="text-lg font-medium mb-4">Vanta Birds Settings</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Controls
            </label>
            <div className="space-y-2">
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  name="vantaSettings.mouseControls"
                  checked={formData.vantaSettings.mouseControls}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    vantaSettings: {
                      ...prev.vantaSettings,
                      mouseControls: e.target.checked
                    }
                  }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2">Mouse Controls</span>
              </label>
              <br />
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  name="vantaSettings.touchControls"
                  checked={formData.vantaSettings.touchControls}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    vantaSettings: {
                      ...prev.vantaSettings,
                      touchControls: e.target.checked
                    }
                  }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2">Touch Controls</span>
              </label>
              <br />
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  name="vantaSettings.gyroControls"
                  checked={formData.vantaSettings.gyroControls}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    vantaSettings: {
                      ...prev.vantaSettings,
                      gyroControls: e.target.checked
                    }
                  }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2">Gyro Controls</span>
              </label>
            </div>
          </div>
          
          <Input
            type="text"
            id="vantaBackgroundColor"
            name="vantaSettings.backgroundColor"
            value={formData.vantaSettings.backgroundColor}
            onChange={handleInputChange}
            label="Background Color"
            placeholder="0x4255a4"
          />
          
          <Input
            type="number"
            id="vantaMinHeight"
            name="vantaSettings.minHeight"
            value={formData.vantaSettings.minHeight.toString()}
            onChange={handleInputChange}
            label="Min Height"
            placeholder="200"
          />
          
          <Input
            type="number"
            id="vantaMinWidth"
            name="vantaSettings.minWidth"
            value={formData.vantaSettings.minWidth.toString()}
            onChange={handleInputChange}
            label="Min Width"
            placeholder="200"
          />
          
          <Input
            type="number"
            id="vantaScale"
            name="vantaSettings.scale"
            value={formData.vantaSettings.scale.toString()}
            onChange={handleInputChange}
            label="Scale"
            placeholder="1.00"
            step="0.01"
          />
          
          <Input
            type="number"
            id="vantaScaleMobile"
            name="vantaSettings.scaleMobile"
            value={formData.vantaSettings.scaleMobile.toString()}
            onChange={handleInputChange}
            label="Scale Mobile"
            placeholder="1.00"
            step="0.01"
          />
        </div>
        
        <div>
          <Input
            type="number"
            id="vantaBirdSize"
            name="vantaSettings.birdSize"
            value={formData.vantaSettings.birdSize.toString()}
            onChange={handleInputChange}
            label="Bird Size"
            placeholder="1.00"
            step="0.01"
          />
          
          <Input
            type="number"
            id="vantaWingSpan"
            name="vantaSettings.wingSpan"
            value={formData.vantaSettings.wingSpan.toString()}
            onChange={handleInputChange}
            label="Wing Span"
            placeholder="30.00"
            step="0.01"
          />
          
          <Input
            type="number"
            id="vantaSpeedLimit"
            name="vantaSettings.speedLimit"
            value={formData.vantaSettings.speedLimit.toString()}
            onChange={handleInputChange}
            label="Speed Limit"
            placeholder="5.00"
            step="0.01"
          />
          
          <Input
            type="number"
            id="vantaSeparation"
            name="vantaSettings.separation"
            value={formData.vantaSettings.separation.toString()}
            onChange={handleInputChange}
            label="Separation"
            placeholder="20.00"
            step="0.01"
          />
          
          <Input
            type="number"
            id="vantaAlignment"
            name="vantaSettings.alignment"
            value={formData.vantaSettings.alignment.toString()}
            onChange={handleInputChange}
            label="Alignment"
            placeholder="20.00"
            step="0.01"
          />
          
          <Input
            type="number"
            id="vantaCohesion"
            name="vantaSettings.cohesion"
            value={formData.vantaSettings.cohesion.toString()}
            onChange={handleInputChange}
            label="Cohesion"
            placeholder="20.00"
            step="0.01"
          />
          
          <Input
            type="number"
            id="vantaQuantity"
            name="vantaSettings.quantity"
            value={formData.vantaSettings.quantity.toString()}
            onChange={handleInputChange}
            label="Quantity"
            placeholder="5.00"
            step="0.01"
          />
        </div>
      </div>
    </>
  );
  
  const renderSeoSettings = () => (
    <>
      <h3 className="text-lg font-medium mb-4">SEO Settings</h3>
      
      <Input
        type="text"
        id="metaTitle"
        name="seoSettings.metaTitle"
        value={formData.seoSettings.metaTitle}
        onChange={handleInputChange}
        label="Meta Title"
        placeholder="Coming Soon"
      />
      
      <div className="mb-4">
        <label htmlFor="metaDescription" className="block text-sm font-medium text-gray-700 mb-1">
          Meta Description
        </label>
        <textarea
          id="metaDescription"
          name="seoSettings.metaDescription"
          value={formData.seoSettings.metaDescription}
          onChange={handleInputChange}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Our new website is on its way"
        />
      </div>
      
      <Input
        type="text"
        id="ogImage"
        name="seoSettings.ogImage"
        value={formData.seoSettings.ogImage}
        onChange={handleInputChange}
        label="Open Graph Image URL"
        placeholder="https://example.com/og-image.jpg"
        error={formData.seoSettings.ogImage && !isValidUrl(formData.seoSettings.ogImage) ? 'Please enter a valid URL' : ''}
      />
    </>
  );
  
  const renderSection = () => {
    switch (section) {
      case 'general':
        return renderGeneralSettings();
      case 'header':
        return renderHeaderSettings();
      case 'content':
        return renderContentSettings();
      case 'footer':
        return renderFooterSettings();
      case 'vanta':
        return renderVantaSettings();
      case 'seo':
        return renderSeoSettings();
      default:
        return null;
    }
  };
  
  return (
    <form onSubmit={handleSubmit}>
      {renderSection()}
      
      <div className="mt-6 flex items-center">
        <Button type="submit" disabled={loading}>
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
        
        {success && (
          <span className="ml-4 text-green-600">
            Changes saved successfully!
          </span>
        )}
        
        {error && (
          <span className="ml-4 text-red-600">
            {error}
          </span>
        )}
      </div>
    </form>
  );
};

export default ConfigurationForm;